/**
 * The lasting record of what the driver has heard, kept across trips. The per-trip `played`
 * table stops a story repeating within a drive; this stops a town being "discovered" again every
 * time tracking starts (Hillsboro played at the start of four trips on 2026-09-22 because home
 * sits inside its 4.6 km radius), and it is the data a future discovery map draws from.
 */

/**
 * What was discovered. Towns and areas are collectibles; a feature (river, forest, wilderness)
 * rests 90 days like a landmark; 'state' and 'region' are reserved for the map.
 */
export type DiscoveryKind = 'town' | 'area' | 'landmark' | 'feature' | 'state' | 'region';

export interface Discovery {
  page_id: number;
  kind: DiscoveryKind;
  title: string;
  /** First time it was heard (ms epoch), the trip, and where the car was. */
  first_at: number;
  first_trip_id: string | null;
  first_lat: number | null;
  first_lon: number | null;
  /** Most recent time it was heard; drives the landmark cooldown. */
  last_at: number;
  /** Times heard. 0 when it only rode along with a neighbour's story (see `via`). */
  times: number;
  /** Page id of the story that covered this one, when it was never spoken itself. */
  via: number | null;
}

/** Ordinary landmarks come back after this long: a season later the Gorge deserves its stories again. */
export const LANDMARK_RETURN_MS = 90 * 86_400_000;

/**
 * Places (towns, area rundowns, later states) play once, ever. Their Wikipedia type is 'city' for
 * towns; area rundowns are places by definition.
 */
export function isPlaceType(wikiType: string | null | undefined): boolean {
  return wikiType === 'city';
}

/**
 * Should a story stay quiet because of the lasting record? A place is done once discovered. A
 * landmark rests for LANDMARK_RETURN_MS after it was last heard. `isPlace` comes from the landmark
 * as it is now, not the stored kind, because rows seeded from old history predate the type.
 */
export function isRetired(d: Pick<Discovery, 'last_at'> | null | undefined, isPlace: boolean, now: number, returnMs = LANDMARK_RETURN_MS): boolean {
  if (!d) return false;
  if (isPlace) return true;
  return now - d.last_at < returnMs;
}

const GENERIC_STEMS = new Set([
  'the', 'north', 'south', 'east', 'west', 'upper', 'lower', 'old', 'new', 'big', 'little', 'great',
  'mount', 'lake', 'fort', 'saint', 'port', 'camp', 'cape', 'point', 'city', 'county', 'state',
  'first', 'united', 'national', 'american', 'oregon', 'washington', 'portland', 'columbia',
]);

/**
 * The first distinctive word of a title, used to spot one place told several ways: "Orenco,
 * Oregon", "Orenco station (TriMet)" and "Orenco Station, Hillsboro, Oregon" all stem to 'orenco'.
 * Null for short or generic words, so "Mount Hood" and "Mount Hood Meadows" are not merged on 'mount'.
 */
export function nameStem(title: string): string | null {
  const w = title.toLowerCase().match(/[\p{L}\p{N}']+/u)?.[0] ?? '';
  if (w.length < 4 || /^\d+$/.test(w) || GENERIC_STEMS.has(w)) return null;
  return w;
}
